/**
 * The PDF's two manager tables:
 *  - דוח תשואות — monthly + YTD return vs benchmark + diff
 *  - תשואה בשקלים — opening/closing balances, flows, organic net change
 * Built from the per-fund series (monthly_return/benchmark_monthly): the
 * workbook's own figures, and for a statement month the ones worked out by
 * withStatementReturns — the page passes the rows through it first. The
 * year-to-date columns follow the sheet's own "שנתי" figures (yearToDate).
 */
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, EmptyState } from './Card.js';
import { FundLogo } from './FundChip.js';
import { cn, fmtPct } from '../utils.js';
import { useMoney } from '../money.js';
import { AltReportMarker } from '../altReports.js';
import { SERIES, bySign, GRID, AXIS, TOOLTIP_PROPS, PCT_AXIS_WIDTH, pctAxisDomain } from '../chartTheme.js';
import { yearToDate } from '../yearToDate.js';
import type { PerformanceRow } from '@agio/core';

/** Latest reporting month across the given rows. */
const lastMonth = (perf: PerformanceRow[]) =>
  perf.reduce<string | null>((a, p) => (!a || p.date > a ? p.date : a), null);

function DiffCell({ v }: { v: number | null }) {
  if (v === null) return <td className="py-2.5">—</td>;
  return <td className={cn('py-2.5 tabular-nums', v >= 0 ? 'text-success' : 'text-destructive')}>{fmtPct(v)}</td>;
}

/** Funds active in the latest month, with their rows grouped. */
function activeFunds(perf: PerformanceRow[]) {
  const last = lastMonth(perf);
  if (!last) return [];
  const ym = last.slice(0, 7);
  const byFund = new Map<string, PerformanceRow[]>();
  for (const p of perf) {
    if (!byFund.has(p.fund_id)) byFund.set(p.fund_id, []);
    byFund.get(p.fund_id)!.push(p);
  }
  return [...byFund.entries()]
    .filter(([, rows]) => rows.some((r) => r.date.slice(0, 7) === ym))
    .map(([fundId, rows]) => ({ fundId, fund: rows[0].fund, rows: rows.sort((a, b) => a.date.localeCompare(b.date)) }));
}

export const managersReturnsHasData = (perf: PerformanceRow[]) => activeFunds(perf).length > 0;

export function ManagersReturnsTable({ perf }: { perf: PerformanceRow[] }) {
  const money = useMoney();
  const funds = activeFunds(perf);
  if (funds.length === 0) return <Card title="דוח תשואות"><EmptyState label="אין נתונים" /></Card>;
  const last = lastMonth(perf)!;
  const year = last.slice(0, 4);
  const ym = last.slice(0, 7);

  const rows = funds.map(({ fundId, fund, rows }) => {
    const monthRow = rows.find((r) => r.date.slice(0, 7) === ym);
    const ytd = rows.filter((r) => r.date.slice(0, 4) === year && r.date <= last);
    return {
      fundId,
      fund,
      volume: monthRow?.asset_volume ?? null,
      monthly: monthRow?.monthly_return ?? null,
      monthlyBench: monthRow?.benchmark_monthly ?? null,
      ytd: yearToDate(ytd, 'return'),
      ytdBench: yearToDate(ytd, 'benchmark'),
    };
  });

  return (
    <Card title={`דוח תשואות · ${last}`}>
      <div className="overflow-x-auto">
        <table className="print-dense-table w-full whitespace-nowrap text-sm">
          <thead>
            <tr className="border-b border-border text-right text-muted-foreground">
              <th className="py-2 pe-2 font-normal">גוף</th>
              <th className="py-2 pe-2 font-normal">סכום</th>
              <th className="py-2 pe-2 font-normal">תשואה חודשית</th>
              <th className="py-2 pe-2 font-normal">מדד ייחוס חודשי</th>
              <th className="py-2 pe-2 font-normal">הפרש</th>
              <th className="py-2 pe-2 font-normal">תשואה שנתית*</th>
              <th className="py-2 pe-2 font-normal">מדד ייחוס שנתי*</th>
              <th className="py-2 pe-2 font-normal">הפרש</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.fundId} className="border-b border-border/50">
                <td className="flex items-center gap-1.5 py-2.5 pe-2 font-medium">
                  <FundLogo name={r.fund} size={18} />
                  {r.fund}
                  <AltReportMarker fundId={r.fundId} />
                </td>
                <td className="py-2.5 pe-2 tabular-nums">{money(r.volume, r.fundId)}</td>
                <td className="py-2.5 pe-2 tabular-nums">{fmtPct(r.monthly)}</td>
                <td className="py-2.5 pe-2 tabular-nums text-muted-foreground">{fmtPct(r.monthlyBench)}</td>
                <DiffCell v={r.monthly !== null && r.monthlyBench !== null ? r.monthly - r.monthlyBench : null} />
                <td className="py-2.5 pe-2 tabular-nums">{fmtPct(r.ytd)}</td>
                <td className="py-2.5 pe-2 tabular-nums text-muted-foreground">{fmtPct(r.ytdBench)}</td>
                <DiffCell v={r.ytd - r.ytdBench} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 border-t border-border pt-4">
        <h3 className="mb-2 text-sm text-muted-foreground">תשואה שנתית מול מדד ייחוס שנתי* · {year}</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={rows} barGap={2}>
            <CartesianGrid {...GRID} vertical={false} />
            <XAxis dataKey="fund" {...AXIS} interval={0} />
            <YAxis orientation="left" tickFormatter={(v: number) => fmtPct(v, 0)} {...AXIS} width={PCT_AXIS_WIDTH} domain={pctAxisDomain(rows.flatMap((r) => [r.ytd, r.ytdBench]))} />
            <Tooltip
              {...TOOLTIP_PROPS}
              formatter={(v: number, name: string) => [fmtPct(v), name === 'ytd' ? 'תשואה שנתית' : 'מדד ייחוס שנתי']}
            />
            <Bar dataKey="ytd" name="ytd" radius={[3, 3, 0, 0]}>
              {rows.map((r) => (
                <Cell key={r.fundId} fill={bySign(r.ytd)} />
              ))}
              <LabelList dataKey="ytd" position="top" formatter={(v: number) => fmtPct(v, 1)} style={{ fill: '#FFFFFF', fontSize: 10 }} />
            </Bar>
            <Bar dataKey="ytdBench" name="ytdBench" fill={SERIES.benchmark} fillOpacity={0.7} radius={[3, 3, 0, 0]}>
              <LabelList dataKey="ytdBench" position="top" formatter={(v: number) => fmtPct(v, 1)} style={{ fill: '#FFFFFF', fontSize: 10 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-2 flex justify-center gap-6 text-xs text-muted-foreground">
          <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.positive }} />תשואה שנתית</span>
          <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.benchmark }} />מדד ייחוס שנתי</span>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">* מתחילת שנה</p>
    </Card>
  );
}

export function ShekelChangesTable({ perf }: { perf: PerformanceRow[] }) {
  const money = useMoney();
  const funds = activeFunds(perf);
  if (funds.length === 0) return <Card title="תשואה בשקלים"><EmptyState label="אין נתונים" /></Card>;
  const last = lastMonth(perf)!;
  const year = Number(last.slice(0, 4));

  const rows = funds.map(({ fundId, fund, rows }) => {
    // Opening balance = last report of the previous year
    const prevYearRows = rows.filter((r) => r.date < `${year}-01-01`);
    const opening = prevYearRows.at(-1)?.asset_volume ?? null;
    const closing = rows.filter((r) => r.date <= last).at(-1)?.asset_volume ?? null;
    const ytd = rows.filter((r) => r.date.slice(0, 4) === String(year) && r.date <= last);
    const deposits = ytd.reduce((a, r) => a + (r.deposits ?? 0), 0);
    const withdrawals = ytd.reduce((a, r) => a + (r.withdrawals ?? 0), 0);
    const net = closing !== null && opening !== null ? closing - opening - deposits + withdrawals : null;
    return { fundId, fund, opening, closing, deposits, withdrawals, net };
  });

  const t = (sel: (r: (typeof rows)[number]) => number | null) => rows.reduce((a, r) => a + (sel(r) ?? 0), 0);
  // Each row in its own fund's currency; the total in theirs only when they all agree.
  const allFunds = rows.map((r) => r.fundId);

  return (
    <Card title={`תשואה בשקלים · ${year}`}>
      <div className="overflow-x-auto">
        <table className="w-full whitespace-nowrap text-sm">
          <thead>
            <tr className="border-b border-border text-right text-muted-foreground">
              <th className="py-2 pe-2 font-normal">גוף</th>
              <th className="py-2 pe-2 font-normal">יתרה ל-31.12.{String(year - 1).slice(2)}</th>
              <th className="py-2 pe-2 font-normal">יתרה לתאריך הדו"ח</th>
              <th className="py-2 pe-2 font-normal">הפקדות</th>
              <th className="py-2 pe-2 font-normal">משיכות</th>
              <th className="py-2 pe-2 font-normal">שינוי בניכוי הפקדות ומשיכות</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.fundId} className="border-b border-border/50">
                <td className="flex items-center gap-1.5 py-2.5 pe-2 font-medium">
                  <FundLogo name={r.fund} size={18} />
                  {r.fund}
                  <AltReportMarker fundId={r.fundId} />
                </td>
                <td className="py-2.5 pe-2 tabular-nums">{money(r.opening, r.fundId)}</td>
                <td className="py-2.5 pe-2 tabular-nums">{money(r.closing, r.fundId)}</td>
                <td className="py-2.5 pe-2 tabular-nums">{r.deposits ? money(r.deposits, r.fundId) : '—'}</td>
                <td className="py-2.5 pe-2 tabular-nums">{r.withdrawals ? money(r.withdrawals, r.fundId) : '—'}</td>
                <td className={cn('py-2.5 pe-2 font-medium tabular-nums', (r.net ?? 0) >= 0 ? 'text-success' : 'text-destructive')}>
                  {money(r.net, r.fundId)}
                </td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="py-2.5 pe-2">סה"כ</td>
              <td className="py-2.5 pe-2 tabular-nums">{money(t((r) => r.opening), allFunds)}</td>
              <td className="py-2.5 pe-2 tabular-nums">{money(t((r) => r.closing), allFunds)}</td>
              <td className="py-2.5 pe-2 tabular-nums">{money(t((r) => r.deposits), allFunds)}</td>
              <td className="py-2.5 pe-2 tabular-nums">{money(t((r) => r.withdrawals), allFunds)}</td>
              <td className="py-2.5 pe-2 tabular-nums text-success">{money(t((r) => r.net), allFunds)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
